import { apiClient } from '@/api/client';
import { DietaryProfile } from '@/types/dietary-profile';
import { Paginated } from '@/types/pagination';
import { mapDietaryProfileWireToDomain } from '../mappers/dietary-profile';
import {
  dietaryProfileWireSchema,
  paginatedDietaryProfileResponseSchema,
  type PaginatedDietaryProfileResponseWire,
} from '../schemas/dietary-profile';

/**
 * Domain-oriented API module for dietary-profile reference data.
 * Read-only: dietary profiles are curated reference data served by the Gateway.
 */

/**
 * Query parameters for listing dietary profiles.
 */
export interface ListDietaryProfilesParams {
  page?: number;
  per_page?: number;
}

/**
 * Map a paginated wire response to a domain Paginated<DietaryProfile>.
 */
function mapPaginatedDietaryProfileWireToDomain(
  wire: PaginatedDietaryProfileResponseWire,
): Paginated<DietaryProfile> {
  return {
    items: wire.items.map(mapDietaryProfileWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

/**
 * Build query string from list parameters.
 */
function buildQueryString(params: ListDietaryProfilesParams): string {
  const queryParams: string[] = [];

  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * List dietary profiles with optional pagination.
 *
 * @param params - Query parameters for pagination
 * @returns Paginated list of domain dietary profiles
 */
export async function listDietaryProfiles(
  params: ListDietaryProfilesParams = {},
): Promise<Paginated<DietaryProfile>> {
  const queryString = buildQueryString(params);
  const endpoint = `/api/v1/dietary-profiles${queryString}`;

  const wire = await apiClient.get<unknown>(endpoint);
  const validated = paginatedDietaryProfileResponseSchema.parse(wire);
  return mapPaginatedDietaryProfileWireToDomain(validated);
}

/**
 * Get a single dietary profile by slug.
 *
 * @param slug - Dietary profile slug
 * @returns DietaryProfile domain model
 */
export async function getDietaryProfile(slug: string): Promise<DietaryProfile> {
  const wire = await apiClient.get<unknown>(`/api/v1/dietary-profiles/${slug}`);
  const validated = dietaryProfileWireSchema.parse(wire);
  return mapDietaryProfileWireToDomain(validated);
}
